import React, { FC, memo, useState, useRef, useEffect } from 'react';
import { trackEvent } from '@src/metrics';
import {
  useCreateVerifiedUserApplicantMutation,
  useRejectVerifiedUserMutation,
  SignalTypeEnum,
  SignalEntityTypeEnum,
  PaginatedVerifiedUserType,
  useSessionQuery,
} from '@src/graphql/generated';
import { recordSignals } from '@src/graphql/signals/actions';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { usePrevious } from '@src/customHooks/usePrevious';
import { getInviteMessage } from '@src/util/getInviteMessage';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { QueryParams } from '@src/containers/Source/interfaces/queryParams.interface';
import { ConnectParams } from '@src/containers/Source/TalentCard/interfaces/connectParams.interface';
import { TalentCarousel } from './TalentCarousel';
import { TalentsSectionProps } from './props';
import './styles.scss';

export const TalentsSection: FC<TalentsSectionProps> = memo(
  ({
    onTalentConfirm,
    onTalentDiscard,
    openingState,
    openingTitle,
    defaultPitchMessage,
    hasMoreVerifiedTalentsResults,
    isVerifiedTalentsFeedFetchMoreLoading,
    isVerifiedTalentsFeedInitialFetchLoading,
    setSwiperActiveIndex,
    swiper,
    setSwiper,
    swiperActiveIndex,
    verifiedTalentsFeed,
    isKeyboardShortcutsDisable,
    isSubscriptionInactive,
  }) => {
    const confirmMessageTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();

    const { data: sessionData } = useSessionQuery();

    const companyName = sessionData?.company.profile.name;

    const [invitedUserIds, setInvitedUserIds] = useState<string[]>([]);

    const prevSwiperActiveIndex = usePrevious(swiperActiveIndex);

    const { queryParams, updateQueryParams } = useQueryParams<QueryParams>();

    const [createVerifiedApplicant] = useCreateVerifiedUserApplicantMutation();

    const [rejectVerifiedUser] = useRejectVerifiedUserMutation();

    const getMetricEventSource = (queryParams: QueryParams): MetricEventSource => {
      if (queryParams.openingId) {
        return MetricEventSource.OPENING_SOURCING_PIPELINE;
      }

      if (queryParams.searchPatterns?.length) {
        return MetricEventSource.SEARCH_SOURCING_PIPELINE;
      }

      return MetricEventSource.DISCOVERY_SOURCING_PIPELINE;
    };

    const goNext = (): void => {
      if (swiper && !isSubscriptionInactive) {
        swiper.slideNext();
      }
    };

    const goBack = (): void => {
      if (swiper && !isSubscriptionInactive) {
        swiper.slidePrev();
      }
    };

    const onConfirmTalent = async (userId: string, params?: ConnectParams): Promise<void> => {
      if (isSubscriptionInactive) {
        return;
      }

      const openingId = params?.openingId;
      const openingTitle = params?.openingTitle;
      const pitch = params?.pitch;

      setInvitedUserIds(prevUserIds => [...prevUserIds, userId]);

      if (userId) {
        await createVerifiedApplicant({
          update: cache => {
            cache.modify({
              fields: {
                recommendedUsers: (cachedRecommendedUsers: PaginatedVerifiedUserType) => {
                  return {
                    ...cachedRecommendedUsers,
                    pagination: {
                      ...cachedRecommendedUsers.pagination,
                      totalCount: cachedRecommendedUsers.pagination.totalCount - 1,
                    },
                  };
                },
              },
              id: cache.identify({
                __typename: 'OpeningType',
                id: openingId,
              }),
            });
          },
          variables: {
            message: pitch || defaultPitchMessage || getInviteMessage(companyName, openingTitle),
            openingId: openingId,
            userId: userId,
          },
        });

        trackEvent({
          context: MetricContext.SOURCE,
          interaction: MetricAction.SEND_MESSAGE_TO_VERIFIED_TALENT,
          payload: {
            'Event Source': getMetricEventSource(queryParams),
            'Opening Id': openingId,
            'User Id': userId,
          },
        });

        if (onTalentConfirm) {
          onTalentConfirm(userId);
        }
      }
    };

    const handleSliderChange = (index: number): void => {
      setSwiperActiveIndex(index);

      if (!verifiedTalentsFeed?.length || !verifiedTalentsFeed[index]) {
        return;
      }

      const userId = verifiedTalentsFeed[index]?.user?.id;
      const userVerificationId = verifiedTalentsFeed[index]?.userVerification?.id;

      recordSignals({
        items: [
          {
            id: userVerificationId,
            type: SignalEntityTypeEnum.USER_VERIFICATION,
          },
        ],
        signalType: SignalTypeEnum.VIEW_USER_VERIFICATION,
      });

      updateQueryParams({
        userId: userId,
      });
    };

    const onDiscardTalent = async (userId: string, openingId?: string): Promise<void> => {
      if (isSubscriptionInactive) {
        return;
      }

      if (swiper) {
        await rejectVerifiedUser({
          update: cache => {
            cache.modify({
              fields: {
                recommendedUsers: (cachedRecommendedUsers: PaginatedVerifiedUserType) => {
                  return {
                    ...cachedRecommendedUsers,
                    pagination: {
                      ...cachedRecommendedUsers.pagination,
                      totalCount: cachedRecommendedUsers.pagination.totalCount - 1,
                    },
                  };
                },
              },
              id: cache.identify({
                __typename: 'OpeningType',
                id: openingId,
              }),
            });
          },
          variables: openingId
            ? {
                openingId: openingId,
                userId: userId,
              }
            : {
                userId: userId,
              },
        });

        if (onTalentDiscard) {
          onTalentDiscard(userId);
        }

        const userActiveIndex = verifiedTalentsFeed?.findIndex(it => it?.user.id === userId);
        handleSliderChange(userActiveIndex + 1);

        trackEvent({
          context: MetricContext.SOURCE,
          interaction: MetricAction.SEND_REJECTION,
          payload: queryParams.openingId
            ? {
                'Event Source': MetricEventSource.OPENING_SOURCING_PIPELINE,
                'Opening Id': queryParams.openingId,
                'User Id': userId,
              }
            : {
                'Event Source': MetricEventSource.DISCOVERY_SOURCING_PIPELINE,
                'User Id': userId,
              },
        });
      }
    };

    useEffect(() => {
      return () => {
        if (confirmMessageTimeout.current) {
          clearTimeout(confirmMessageTimeout.current);
        }
      };
    }, []);

    useEffect(() => {
      if (
        !isVerifiedTalentsFeedInitialFetchLoading &&
        !isVerifiedTalentsFeedFetchMoreLoading &&
        verifiedTalentsFeed &&
        swiperActiveIndex !== prevSwiperActiveIndex
      ) {
        const userVerificationState = verifiedTalentsFeed[swiperActiveIndex]?.userVerification?.state;

        trackEvent({
          context: MetricContext.SOURCE,
          interaction: MetricAction.OPEN_CANDIDATE_PROFILE_VIEW,
          payload: {
            'Event Source': getMetricEventSource(queryParams),
            'Opening Id': queryParams.openingId,
            'User Verification State': userVerificationState,
          },
        });
      }
    }, [swiperActiveIndex]);

    return (
      <div className="ta-section-wrapper">
        <div className="ta-section-wrapper__carousel">
          <TalentCarousel
            goBack={goBack}
            goNext={goNext}
            groupedTalentsKey={queryParams?.groupedTalentsKey}
            hasMoreResults={hasMoreVerifiedTalentsResults}
            invitedUserIds={invitedUserIds}
            isFetchMoreLoading={isVerifiedTalentsFeedFetchMoreLoading}
            isInitialFetchLoading={isVerifiedTalentsFeedInitialFetchLoading}
            isKeyboardShortcutsDisable={isKeyboardShortcutsDisable}
            isSwiperSet={!!swiper && !swiper?.destroyed}
            onConfirmTalent={(userId, params) => onConfirmTalent(userId, params)}
            onDiscardTalent={onDiscardTalent}
            onSlideChange={swiper => handleSliderChange(swiper.activeIndex)}
            onStartReview={() => swiper?.slideNext()}
            openingId={queryParams.openingId}
            openingState={openingState}
            openingTitle={openingTitle}
            setSwiper={swiper => setSwiper(swiper)}
            swiperActiveIndex={swiperActiveIndex}
            usersData={verifiedTalentsFeed}
          />
        </div>
      </div>
    );
  },
);

TalentsSection.displayName = 'TalentsSection';
